<script lang="ts">
  import { filterTransactions } from '../lib/analytics/transactions';
  import type { DataStore } from '../lib/data/store';
  import type { SearchableTransaction, TransactionFilters, TxType } from '../lib/data/types';
  import FilterBar from '../lib/filters/FilterBar.svelte';
  import { formatCents, formatDate, signedClass } from '../lib/presentation/format';
  import type { AppState } from '../lib/state/app.svelte';
  import TransactionDetail from '../lib/transactions/TransactionDetail.svelte';
  import Button from '../lib/ui/Button.svelte';
  import EmptyState from '../lib/ui/EmptyState.svelte';
  import Icon from '../lib/ui/Icon.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';

  type SortKey = 'date' | 'description' | 'amount' | 'type' | 'account' | 'category' | 'group';
  type SortRule = { key: SortKey; direction: 'asc' | 'desc' };
  type ColumnKey = Exclude<SortKey, 'date'> | 'date' | 'counter';

  let { store, appState }: { store: DataStore; appState: AppState } = $props();
  const initialParams = new URLSearchParams(window.location.search);
  let text = $state('');
  let debouncedText = $state('');
  let minAmount = $state(initialParams.get('min') ?? '');
  let maxAmount = $state(initialParams.get('max') ?? '');
  let sign = $state<'all' | 'positive' | 'negative'>(
    (initialParams.get('sign') as 'positive' | 'negative' | null) ?? 'all',
  );
  let types = $state<TxType[]>((initialParams.get('types')?.split(',') as TxType[]) ?? []);
  let counterAccountId = $state(initialParams.get('counter') ?? '');
  let parentCategoryId = $state(initialParams.get('parentCategory') ?? '');
  let hasDescription = $state<'all' | 'yes' | 'no'>(
    (initialParams.get('description') as 'yes' | 'no' | null) ?? 'all',
  );
  let weekend = $state<'all' | 'yes' | 'no'>(
    (initialParams.get('weekend') as 'yes' | 'no' | null) ?? 'all',
  );
  let daysOfWeek = $state<number[]>(initialParams.get('days')?.split(',').map(Number) ?? []);
  let transactionId = $state(initialParams.get('transaction') ?? '');
  let transferId = $state(initialParams.get('transfer') ?? '');
  let pageSize = $state(Number(initialParams.get('pageSize') ?? 25));
  let page = $state(1);
  let selected = $state<SearchableTransaction | null>(null);
  let visibleColumns = $state<ColumnKey[]>([
    'date',
    'description',
    'amount',
    'type',
    'account',
    'counter',
    'category',
    'group',
  ]);
  let sortRules = $state<SortRule[]>(parseSort(initialParams.get('sort')));

  const columnLabels: Record<ColumnKey, string> = {
    date: 'Data',
    description: 'Descrizione',
    amount: 'Importo',
    type: 'Tipo',
    account: 'Conto',
    counter: 'Controconto',
    category: 'Categoria',
    group: 'Gruppo',
  };
  const dayLabels = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  const parentIncomeCategories = $derived(
    store.core.categories.filter(
      (category) => category.parent_id === null && category.kind === 'income',
    ),
  );
  const parentExpenseCategories = $derived(
    store.core.categories.filter(
      (category) => category.parent_id === null && category.kind === 'expense',
    ),
  );
  let advancedFilterCount = $derived(
    [
      minAmount !== '',
      maxAmount !== '',
      sign !== 'all',
      types.length > 0,
      counterAccountId !== '',
      parentCategoryId !== '',
      hasDescription !== 'all',
      weekend !== 'all',
      daysOfWeek.length > 0,
      transactionId !== '',
      transferId !== '',
    ].filter(Boolean).length,
  );

  function parseSort(value: string | null): SortRule[] {
    if (!value) return [{ key: 'date', direction: 'desc' }];
    return value.split(',').flatMap((part) => {
      const [key, direction] = part.split(':');
      return ['date', 'description', 'amount', 'type', 'account', 'category', 'group'].includes(
        key ?? '',
      ) &&
        (direction === 'asc' || direction === 'desc')
        ? [{ key: key as SortKey, direction }]
        : [];
    });
  }

  let filters = $derived.by((): TransactionFilters => ({
    from: appState.fromDate ?? store.core.calendar[0]!.date,
    to: appState.toDate ?? store.core.calendar.at(-1)!.date,
    text: debouncedText,
    includeUncategorized: appState.includeUncategorized,
    ...(appState.accountFilterActive ? { accountIds: new Set(appState.accountIds) } : {}),
    ...(appState.categoryFilterActive ? { categoryIds: new Set(appState.categoryIds) } : {}),
    ...(types.length ? { types: new Set(types) } : {}),
    ...(counterAccountId ? { counterAccountIds: new Set([counterAccountId]) } : {}),
    ...(parentCategoryId ? { parentCategoryIds: new Set([parentCategoryId]) } : {}),
    ...(minAmount ? { minAmountCents: Math.round(Number(minAmount.replace(',', '.')) * 100) } : {}),
    ...(maxAmount ? { maxAmountCents: Math.round(Number(maxAmount.replace(',', '.')) * 100) } : {}),
    ...(sign !== 'all' ? { sign } : {}),
    ...(hasDescription !== 'all' ? { hasDescription: hasDescription === 'yes' } : {}),
    ...(daysOfWeek.length ? { daysOfWeek: new Set(daysOfWeek) } : {}),
    ...(weekend !== 'all' ? { weekend: weekend === 'yes' } : {}),
    ...(transactionId ? { transactionId } : {}),
    ...(transferId ? { transferId } : {}),
  }));
  let filtered = $derived(filterTransactions(store.transactions, filters));
  let sorted = $derived([...filtered].sort(compareTransactions));
  let totalPages = $derived(Math.max(1, Math.ceil(sorted.length / pageSize)));
  let pageRows = $derived(sorted.slice((page - 1) * pageSize, page * pageSize));
  let totalAmount = $derived(
    filtered.reduce((sum, transaction) => sum + transaction.amount_cents, 0),
  );

  $effect(() => {
    if (page > totalPages) page = totalPages;
  });

  $effect(() => {
    const nextText = text;
    const timer = window.setTimeout(() => {
      debouncedText = nextText;
      page = 1;
    }, 180);
    return () => window.clearTimeout(timer);
  });

  $effect(() => {
    // Il testo libero è intenzionalmente escluso dall'URL perché può essere sensibile.
    const url = new URL(appState.toUrl('transactions'), window.location.origin);
    if (minAmount) url.searchParams.set('min', minAmount);
    if (maxAmount) url.searchParams.set('max', maxAmount);
    if (sign !== 'all') url.searchParams.set('sign', sign);
    if (types.length) url.searchParams.set('types', types.join(','));
    if (counterAccountId) url.searchParams.set('counter', counterAccountId);
    if (parentCategoryId) url.searchParams.set('parentCategory', parentCategoryId);
    if (hasDescription !== 'all') url.searchParams.set('description', hasDescription);
    if (weekend !== 'all') url.searchParams.set('weekend', weekend);
    if (daysOfWeek.length) url.searchParams.set('days', daysOfWeek.join(','));
    if (transactionId) url.searchParams.set('transaction', transactionId);
    if (transferId) url.searchParams.set('transfer', transferId);
    url.searchParams.set(
      'sort',
      sortRules.map((rule) => `${rule.key}:${rule.direction}`).join(','),
    );
    url.searchParams.set('pageSize', String(pageSize));
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  });

  function valueFor(transaction: SearchableTransaction, key: SortKey): string | number {
    switch (key) {
      case 'date':
        return transaction.date;
      case 'description':
        return transaction.description ?? '';
      case 'amount':
        return transaction.amount_cents;
      case 'type':
        return transaction.type;
      case 'account':
        return store.accountLabel(transaction.account_id);
      case 'category':
        return store.categoryLabel(transaction.category_id);
      case 'group':
        return store.groupLabel(transaction.account_group_id);
    }
  }

  function compareTransactions(left: SearchableTransaction, right: SearchableTransaction): number {
    for (const rule of sortRules) {
      const leftValue = valueFor(left, rule.key);
      const rightValue = valueFor(right, rule.key);
      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), 'it');
      if (comparison !== 0) return rule.direction === 'asc' ? comparison : -comparison;
    }
    return right.id.localeCompare(left.id);
  }

  function sortBy(event: MouseEvent, key: SortKey): void {
    const current = sortRules.find((rule) => rule.key === key);
    const next: SortRule = { key, direction: current?.direction === 'asc' ? 'desc' : 'asc' };
    sortRules = event.shiftKey ? [...sortRules.filter((rule) => rule.key !== key), next] : [next];
  }

  function sortIndex(key: SortKey): number {
    return sortRules.findIndex((rule) => rule.key === key) + 1;
  }

  function toggleType(type: TxType): void {
    types = types.includes(type) ? types.filter((item) => item !== type) : [...types, type];
    page = 1;
  }

  function toggleDay(day: number): void {
    daysOfWeek = daysOfWeek.includes(day)
      ? daysOfWeek.filter((item) => item !== day)
      : [...daysOfWeek, day];
    page = 1;
  }

  function toggleColumn(column: ColumnKey): void {
    visibleColumns = visibleColumns.includes(column)
      ? visibleColumns.filter((item) => item !== column)
      : [...visibleColumns, column];
  }

  function resetAdvanced(): void {
    minAmount = '';
    maxAmount = '';
    sign = 'all';
    types = [];
    counterAccountId = '';
    parentCategoryId = '';
    hasDescription = 'all';
    weekend = 'all';
    daysOfWeek = [];
    transactionId = '';
    transferId = '';
    page = 1;
  }

  function downloadCsv(): void {
    const escape = (value: string | number) => `"${String(value).replaceAll('"', '""')}"`;
    const rows = [
      [
        'data',
        'descrizione',
        'importo_cents',
        'tipo',
        'conto',
        'controconto',
        'categoria',
        'gruppo',
      ],
      ...sorted.map((transaction) => [
        transaction.date,
        transaction.description ?? '',
        transaction.amount_cents,
        transaction.type,
        store.accountLabel(transaction.account_id),
        transaction.counter_account_id ? store.accountLabel(transaction.counter_account_id) : '',
        store.categoryLabel(transaction.category_id),
        store.groupLabel(transaction.account_group_id),
      ]),
    ];
    const blob = new Blob([rows.map((row) => row.map(escape).join(',')).join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `transazioni-${filters.from}-${filters.to}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }
</script>

<main id="main-content" class="page transactions-page">
  <PageHeader
    title="Transazioni"
    description="Esplora, ordina ed esporta ogni movimento senza perdere il contesto della dashboard."
    state={appState}
  />
  <FilterBar state={appState} />

  <section class="card transaction-filters">
    <div class="primary-search">
      <label class="search-field"
        ><Icon name="search" size={18} /><span class="sr-only">Cerca descrizione</span><input
          type="search"
          bind:value={text}
          oninput={() => (page = 1)}
          placeholder="Cerca nella descrizione…"
        /></label
      >
      <div class="result-summary" aria-live="polite">
        <strong>{filtered.length}</strong> risultati ·
        <span class={signedClass(totalAmount)}>{formatCents(totalAmount)}</span>
      </div>
      <Button onclick={downloadCsv}><Icon name="download" size={17} /> CSV</Button>
    </div>
    <details class="advanced-filters">
      <summary>
        <span>Filtri avanzati</span>
        {#if advancedFilterCount}<span class="advanced-count">{advancedFilterCount} attivi</span
          >{/if}
      </summary>
      <div class="advanced-panel">
        <section class="advanced-section" aria-labelledby="advanced-values-title">
          <h3 id="advanced-values-title">Importo e contenuto</h3>
          <div class="advanced-grid">
            <label class="control-field"
              ><span>Importo minimo (€)</span><input
                type="text"
                inputmode="decimal"
                autocomplete="off"
                placeholder="0,00"
                bind:value={minAmount}
              /></label
            >
            <label class="control-field"
              ><span>Importo massimo (€)</span><input
                type="text"
                inputmode="decimal"
                autocomplete="off"
                placeholder="Nessun limite"
                bind:value={maxAmount}
              /></label
            >
            <label class="control-field"
              ><span>Segno</span><select bind:value={sign}
                ><option value="all">Qualsiasi</option><option value="positive">Positivo</option
                ><option value="negative">Negativo</option></select
              ></label
            >
            <label class="control-field"
              ><span>Descrizione</span><select bind:value={hasDescription}
                ><option value="all">Qualsiasi</option><option value="yes">Presente</option><option
                  value="no">Assente</option
                ></select
              ></label
            >
          </div>
        </section>

        <section class="advanced-section" aria-labelledby="advanced-relations-title">
          <h3 id="advanced-relations-title">Relazioni</h3>
          <div class="advanced-grid">
            <label class="control-field"
              ><span>Controconto</span><select bind:value={counterAccountId}
                ><option value="">Tutti i controconti</option
                >{#each store.core.accounts as account}<option value={account.id}
                    >{account.name}</option
                  >{/each}</select
              ></label
            >
            <label class="control-field"
              ><span>Categoria principale</span><select bind:value={parentCategoryId}
                ><option value="">Tutte le categorie</option><optgroup label="Entrate"
                  >{#each parentIncomeCategories as category}<option value={category.id}
                      >{category.name}</option
                    >{/each}</optgroup
                ><optgroup label="Uscite"
                  >{#each parentExpenseCategories as category}<option value={category.id}
                      >{category.name}</option
                    >{/each}</optgroup
                ></select
              ></label
            >
            <label class="control-field"
              ><span>Weekend</span><select bind:value={weekend}
                ><option value="all">Qualsiasi giorno</option><option value="yes"
                  >Solo weekend</option
                ><option value="no">Escludi weekend</option></select
              ></label
            >
          </div>
        </section>

        <section class="advanced-section" aria-labelledby="advanced-types-title">
          <h3 id="advanced-types-title">Tipo e giorni</h3>
          <div class="advanced-choice-grid">
            <fieldset class="choice-fieldset">
              <legend>Tipo di movimento</legend
              >{#each [['income', 'Entrata'], ['expense', 'Spesa'], ['transfer_in', 'Trasferimento in entrata'], ['transfer_out', 'Trasferimento in uscita']] as type}<label
                  ><input
                    type="checkbox"
                    checked={types.includes(type[0] as TxType)}
                    onchange={() => toggleType(type[0] as TxType)}
                  />
                  <span>{type[1]}</span></label
                >{/each}
            </fieldset>
            <fieldset class="choice-fieldset compact-choices">
              <legend>Giorni della settimana</legend>{#each dayLabels as day, index}<label
                  ><input
                    type="checkbox"
                    checked={daysOfWeek.includes(index)}
                    onchange={() => toggleDay(index)}
                  />
                  <span>{day}</span></label
                >{/each}
            </fieldset>
          </div>
        </section>

        <section class="advanced-section" aria-labelledby="advanced-diagnostics-title">
          <h3 id="advanced-diagnostics-title">Identificativi</h3>
          <div class="advanced-grid">
            <label class="control-field"
              ><span>ID transazione</span><input
                type="text"
                autocomplete="off"
                placeholder="Cerca per ID"
                bind:value={transactionId}
              /></label
            >
            <label class="control-field"
              ><span>ID trasferimento</span><input
                type="text"
                autocomplete="off"
                placeholder="Cerca per ID"
                bind:value={transferId}
              /></label
            >
          </div>
        </section>

        <footer class="advanced-actions">
          <Button variant="ghost" disabled={advancedFilterCount === 0} onclick={resetAdvanced}
            >Ripristina filtri avanzati</Button
          >
        </footer>
      </div>
    </details>
  </section>

  <section class="card transaction-results">
    <div class="table-toolbar">
      <div>
        <label
          >Righe <select bind:value={pageSize} onchange={() => (page = 1)}
            ><option value={10}>10</option><option value={25}>25</option><option value={50}
              >50</option
            ><option value={100}>100</option></select
          ></label
        >
      </div>
      <details class="column-picker">
        <summary>Colonne</summary>
        <div>
          {#each Object.entries(columnLabels) as [key, label]}<label
              ><input
                type="checkbox"
                checked={visibleColumns.includes(key as ColumnKey)}
                onchange={() => toggleColumn(key as ColumnKey)}
              />
              {label}</label
            >{/each}
        </div>
      </details>
    </div>

    {#if pageRows.length}
      <!-- svelte-ignore a11y_no_noninteractive_tabindex (scrollable region must be keyboard reachable) -->
      <div
        class="transaction-table scroll-region"
        tabindex="0"
        role="region"
        aria-label="Tabella transazioni"
      >
        <table>
          <thead
            ><tr>
              {#each Object.entries(columnLabels) as [key, label]}
                {#if visibleColumns.includes(key as ColumnKey)}
                  <th
                    >{#if key === 'counter'}{label}{:else}<button
                        type="button"
                        aria-label={`Ordina per ${label}`}
                        onclick={(event) => sortBy(event, key as SortKey)}
                        >{label}{#if sortIndex(key as SortKey)}
                          <span aria-label={`priorità ${sortIndex(key as SortKey)}`}
                            >{sortRules.find((rule) => rule.key === key)?.direction === 'asc'
                              ? '↑'
                              : '↓'}{sortIndex(key as SortKey)}</span
                          >{/if}</button
                      >{/if}</th
                  >
                {/if}
              {/each}
            </tr></thead
          >
          <tbody
            >{#each pageRows as transaction (transaction.id)}<tr
                tabindex="0"
                onclick={() => (selected = transaction)}
                onkeydown={(event) =>
                  (event.key === 'Enter' || event.key === ' ') && (selected = transaction)}
              >
                {#if visibleColumns.includes('date')}<td>{formatDate(transaction.date)}</td>{/if}
                {#if visibleColumns.includes('description')}<td
                    class="description-cell"
                    title={transaction.description ?? 'Senza descrizione'}
                    >{transaction.description ?? 'Senza descrizione'}</td
                  >{/if}
                {#if visibleColumns.includes('amount')}<td
                    class="amount-cell {signedClass(transaction.amount_cents)}"
                    >{formatCents(transaction.amount_cents)}</td
                  >{/if}
                {#if visibleColumns.includes('type')}<td
                    ><span class="type-badge {transaction.flow}"
                      >{transaction.type.replace('_', ' ')}</span
                    ></td
                  >{/if}
                {#if visibleColumns.includes('account')}<td
                    >{store.accountLabel(transaction.account_id)}</td
                  >{/if}
                {#if visibleColumns.includes('counter')}<td
                    >{transaction.counter_account_id
                      ? store.accountLabel(transaction.counter_account_id)
                      : '—'}</td
                  >{/if}
                {#if visibleColumns.includes('category')}<td
                    >{store.categoryLabel(transaction.category_id)}</td
                  >{/if}
                {#if visibleColumns.includes('group')}<td
                    >{store.groupLabel(transaction.account_group_id)}</td
                  >{/if}
              </tr>{/each}</tbody
          >
        </table>
      </div>

      <div class="transaction-list">
        {#each pageRows as transaction (transaction.id)}
          <button type="button" onclick={() => (selected = transaction)}>
            <span class="ledger-copy"
              ><strong>{transaction.description ?? 'Senza descrizione'}</strong><small
                >{formatDate(transaction.date)} · {store.accountLabel(transaction.account_id)} · {store.categoryLabel(
                  transaction.category_id,
                )}</small
              ></span
            >
            <span class="ledger-amount {signedClass(transaction.amount_cents)}"
              >{formatCents(transaction.amount_cents)}</span
            >
          </button>
        {/each}
      </div>

      <nav class="pagination" aria-label="Paginazione transazioni">
        <Button disabled={page <= 1} onclick={() => (page -= 1)}>Precedente</Button>
        <span>Pagina {page} di {totalPages}</span>
        <Button disabled={page >= totalPages} onclick={() => (page += 1)}>Successiva</Button>
      </nav>
    {:else}
      <EmptyState action={resetAdvanced} />
    {/if}
  </section>
</main>

{#if selected}<TransactionDetail
    transaction={selected}
    {store}
    close={() => (selected = null)}
  />{/if}
